import React, { useEffect, useState } from 'react';
import { Collapsible } from '@base-ui/react/collapsible';
import { ChevronRight } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@hooks/useAuth';
import { usePendingReviewCount } from '@hooks/usePendingReviewCount';
import {
  Sidebar as UISidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  SIDEBAR_FOOTER_ITEMS,
  SIDEBAR_SECTIONS,
  isNavItemActive,
  type SidebarNavItem as NavItem,
  type SidebarSection,
} from './sidebar-config';
import SidebarBrand from './SidebarBrand';
import SidebarUserMenu from './SidebarUserMenu';

function NavSection({
  section,
  renderItem,
}: {
  section: SidebarSection;
  renderItem: (item: NavItem, nested?: boolean) => React.ReactNode;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { state, isMobile } = useSidebar();
  const [open, setOpen] = useState(true);
  const active = section.items.some((item) => isNavItemActive(item.href, pathname));

  // Following a deep link or browser history must reveal the current page,
  // even when the operator previously closed its group.
  useEffect(() => {
    if (active) setOpen(true);
  }, [pathname, active]);

  // Preserve every destination and its tooltip in the desktop icon rail.
  // A phone sheet always uses the expanded tree, regardless of desktop state.
  if ((!isMobile && state === 'collapsed') || section.labelKey === null) {
    return <SidebarMenu>{section.items.map((item) => renderItem(item))}</SidebarMenu>;
  }

  return (
    <SidebarMenu>
      <Collapsible.Root open={open} onOpenChange={setOpen} render={<SidebarMenuItem />}>
        <Collapsible.Trigger
          render={<SidebarMenuButton />}
          data-testid="nav-group-label"
          className="group/collapsible"
        >
          <span>{t(section.labelKey)}</span>
          <ChevronRight className="ml-auto transition-transform group-aria-expanded/collapsible:rotate-90" />
        </Collapsible.Trigger>
        <Collapsible.Panel>
          <SidebarMenuSub>{section.items.map((item) => renderItem(item, true))}</SidebarMenuSub>
        </Collapsible.Panel>
      </Collapsible.Root>
    </SidebarMenu>
  );
}

/**
 * The operator inbox's pending count (#2476 S4): open messages raised today.
 * Mounted only on an ENABLED inbox row, so a seat without `review:read` never
 * fires the query (the list endpoint would answer 403). It sits inside the
 * row's link, shaped like shadcn's `SidebarMenuBadge`, and hides with the
 * labels when the sidebar collapses to icons.
 */
function PendingReviewBadge() {
  const total = usePendingReviewCount().data?.total ?? 0;
  if (total <= 0) return null;
  return (
    <span
      data-testid="nav-pending-review-count"
      className="ml-auto flex h-5 min-w-5 shrink-0 items-center justify-center rounded-md bg-warning/12 px-1 text-xs font-medium tabular-nums text-warning group-data-[collapsible=icon]:hidden"
    >
      {total}
    </span>
  );
}

/**
 * The app sidebar on shadcn's Sidebar (#2511): brand header, six labelled
 * collapsible groups of bordered submenus, docs and the user block in the footer, a rail that
 * collapses it to icons (rows then show their name in a tooltip). Below `md`
 * the same tree renders as a sheet that the phone tab bar's 更多 opens.
 */
const Sidebar: React.FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const { isMobile, setOpenMobile } = useSidebar();
  const perms: string[] = (user as { permissions?: string[] } | null)?.permissions ?? [];

  // Migration-era nav: every item is rendered so the information
  // architecture stays complete; an item whose backend permission the
  // user does not hold renders DISABLED (greyed, non-navigable) rather
  // than hidden. For admin, `/api/permissions` returns the full backend
  // catalog, so every built module is enabled while not-yet-built modules
  // (their `<module>:<action>` key absent from the catalog) stay disabled
  // — a truthful signal of what this milestone's backend actually serves.
  // Uniform-by-permission: no role short-circuit here, so admin visibility
  // genuinely exercises `/api/permissions` instead of masking it. Explicit
  // dead entries remain disabled independently of the permission catalog.
  const isEnabled = (item: NavItem) =>
    !item.disabled && (!item.permission || perms.includes(item.permission));

  // The phone sheet closes once a row is followed; the desktop sidebar stays.
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  const renderNavItem = (item: NavItem, nested = false) => {
    const label = t(item.titleKey);
    const Item = nested ? SidebarMenuSubItem : SidebarMenuItem;
    const MenuButton = nested ? SidebarMenuSubButton : SidebarMenuButton;
    const tooltipProps = nested ? {} : { tooltip: label };
    if (!isEnabled(item)) {
      // Not an anchor: a disabled row must not be followable, and the
      // landing-consistency test tells enabled rows apart by their <a>.
      return (
        <Item key={item.href}>
          <MenuButton
            render={<div />}
            aria-disabled="true"
            data-testid={`nav-disabled-${item.href}`}
            {...tooltipProps}
          >
            {item.icon}
            <span>{label}</span>
          </MenuButton>
        </Item>
      );
    }
    const isActive = isNavItemActive(item.href, location.pathname);
    return (
      <Item key={item.href}>
        <MenuButton
          render={<Link to={item.href} />}
          isActive={isActive}
          {...tooltipProps}
          onClick={closeOnMobile}
          // Exposed so a test can assert WHICH row is lit without reaching into
          // Tailwind class strings, which are styling and may change freely.
          data-nav-active={isActive ? 'true' : 'false'}
          aria-current={isActive ? 'page' : undefined}
        >
          {item.icon}
          <span className="truncate">{label}</span>
          {item.pendingReviewCount && <PendingReviewBadge />}
        </MenuButton>
      </Item>
    );
  };

  return (
    <UISidebar collapsible="icon">
      <SidebarHeader>
        {/* shadcn sidebar-07 header (#2514): a large menu button that shrinks
            to the brand mark when collapsed. It links to the landing page, the
            one thing it can honestly do; no chevron, as there is no tenant to
            switch to. */}
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              render={<Link to="/" />}
              onClick={closeOnMobile}
              data-testid="sidebar-brand"
            >
              <SidebarBrand />
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <nav className="flex min-w-0 flex-col">
          {SIDEBAR_SECTIONS.map((section) => {
            if (section.adminOnly && !isAdmin) return null;
            if (section.items.length === 0) return null;
            const key = section.labelKey ?? section.items.map((i) => i.href).join('|');
            return (
              <SidebarGroup key={key}>
                <NavSection section={section} renderItem={renderNavItem} />
              </SidebarGroup>
            );
          })}
        </nav>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          {SIDEBAR_FOOTER_ITEMS.map((item) => {
            const isActive = isNavItemActive(item.href, location.pathname);
            const label = t(item.titleKey);
            return (
              <SidebarMenuItem key={item.href}>
                <SidebarMenuButton
                  render={<Link to={item.href} />}
                  isActive={isActive}
                  tooltip={label}
                  onClick={closeOnMobile}
                  aria-label={label}
                  data-nav-active={isActive ? 'true' : 'false'}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.icon}
                  <span>{label}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          })}
          <SidebarMenuItem>
            <SidebarUserMenu />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </UISidebar>
  );
};

export default Sidebar;
